import { Channel } from '@anycable/web';

import { CableSocket } from './ws';

// DIAGNOSTIC (temporary): BackgroundJobProgressChannel gets its own dedicated
// WS connection instead of sharing the app's single multiplexed connection;
// every other channel keeps using the one shared connection as before. Used
// to prove/disprove whether sharing the connection with other channels (e.g.
// HomeChannel) is why BackgroundJobProgressChannel's `message` events go
// missing. Revert to always using `_consumer` once that's confirmed with the
// backend.
const DEDICATED_CHANNELS = ['BackgroundJobProgressChannel'];

class AnyCableSocketManager {
  _consumer = CableSocket();
  _dedicatedConsumers = {};
  _subscribers = {};

  constructor() {
    if (!this._consumer.transport.connected) {
      this._consumer.connect().catch(() => null);
    }
    AnyCableSocketManager.instance = this;
  }

  static getInstance() {
    if (AnyCableSocketManager.instance) {
      return AnyCableSocketManager.instance;
    }

    return new AnyCableSocketManager();
  }

  isDedicated(channel) {
    return DEDICATED_CHANNELS.includes(channel?.channel);
  }

  getConsumer(channel) {
    if (!this.isDedicated(channel)) return this._consumer;

    const key = JSON.stringify(channel);
    if (!this._dedicatedConsumers[key]) {
      this._dedicatedConsumers[key] = CableSocket();
    }
    return this._dedicatedConsumers[key];
  }

  connect(channel, successFallback) {
    const consumer = this.getConsumer(channel);
    if (consumer.transport.connected) {
      return successFallback();
    }
    consumer
      .connect()
      .then(() => successFallback())
      .catch(() => null);
  }

  subscribe(channel, callback, callbackConnected) {
    this.connect(channel, () => {
      if (this._subscribers?.[JSON.stringify(channel)]) return;
      const ch = new Channel(channel);
      this.getConsumer(channel).subscribe(ch);
      ch.once('connect', () => {
        callbackConnected && callbackConnected();
        this._subscribers[JSON.stringify(channel)] = ch;
      });
      callback && ch.on('message', (msg) => callback(msg));
    });
  }

  getSubscriptions(channel) {
    return this._subscribers?.[JSON.stringify(channel)];
  }

  // eslint-disable-next-line max-params
  perform(channel, action, data, callback) {
    const subscriptions = this.getSubscriptions(channel);
    if (!subscriptions || subscriptions?.state != 'connected') return;
    subscriptions.perform(action, data);
    callback && callback();
  }

  unsubscribe(channel, autoDisconnect = true) {
    const subscriptions = this.getSubscriptions(channel);
    if (!subscriptions) return;

    const consumer = this.getConsumer(channel);
    if (subscriptions.state != 'closed') {
      consumer.unsubscribe(subscriptions);
    }

    delete this._subscribers?.[JSON.stringify(channel)];
    if (!autoDisconnect) return;
    this.handleAutoDisconnect(channel);
  }

  unsubscribeMatch(identifier, autoDisconnect = true) {
    const subscriptions = this._subscribers;

    const keys = Object.keys(subscriptions);
    if (!identifier) return;
    keys.forEach((id) => {
      if (!id.includes(identifier)) return;
      this.unsubscribe(JSON.parse(id), autoDisconnect);
    });
  }

  handleAutoDisconnect(channel) {
    if (this.isDedicated(channel)) {
      const key = JSON.stringify(channel);
      const consumer = this._dedicatedConsumers[key];
      if (!consumer) return;
      consumer.disconnect();
      delete this._dedicatedConsumers[key];
      return;
    }

    const sharedRemaining = Object.keys(this._subscribers).some(
      (id) => !this.isDedicated(JSON.parse(id))
    );
    if (!sharedRemaining) this._consumer.disconnect();
  }
}

// class DefaultCableSocketManager {
//   _consumer = CableSocket();

//   constructor() {
//     if (!this._consumer.connection.isOpen()) {
//       this._consumer.connection.open();
//     }

//     DefaultCableSocketManager.instance = this;
//   }

//   static getInstance() {
//     if (DefaultCableSocketManager.instance) {
//       return DefaultCableSocketManager.instance;
//     }

//     return new DefaultCableSocketManager();
//   }

//   subscribe(channel, callback, callbackConnected) {
//     if (this.getSubscriptions(channel).length > 0) return;
//     this._consumer.subscriptions.create(channel, {
//       ...(callback && { received: callback }),
//       ...(callbackConnected && { connected: callbackConnected })
//     });
//   }

//   getSubscriptions(channel) {
//     if (!this._consumer) return [];

//     const identifier = typeof channel == 'object' ? channel : { channel };
//     return this._consumer.subscriptions.findAll(JSON.stringify(identifier));
//   }

//   perform(channel, action, data, callback) {
//     const subscriptions = this.getSubscriptions(channel);
//     if (!action) return;
//     if (subscriptions.length == 0) {
//       this.subscribe(channel, () => null);
//       this.perform(channel, action, data, callback);
//       this.unsubscribe(channel);
//       return;
//     }
//     subscriptions[0].perform(action, data);
//     callback && callback();
//   }

//   unsubscribe(channel, autoDisconnect = true) {
//     const subscriptions = this.getSubscriptions(channel);
//     if (subscriptions.length == 0) return;

//     subscriptions[0].unsubscribe();
//     if (!autoDisconnect) return;
//     this.disconnect();
//   }

//   unsubscribeMatch(identifier) {
//     const subscriptions = this._consumer.subscriptions.subscriptions.filter(
//       (subscription) => subscription.identifier.includes(identifier)
//     );
//     subscriptions.forEach((subscription) => {
//       subscription.unsubscribe();
//     });
//     this.disconnect();
//   }

//   disconnect(force) {
//     if (force) {
//       this._consumer.disconnect();
//       return;
//     }
//     const subscriptions = this._consumer.subscriptions.subscriptions;
//     if (subscriptions.length == 0) this._consumer.disconnect();
//   }
// }

export default AnyCableSocketManager;
